import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Detect if we're on a custom domain (not preview.emergentagent.com)
const isCustomDomain = typeof window !== 'undefined' && 
  !window.location.hostname.includes('preview.emergentagent.com') &&
  !window.location.hostname.includes('localhost');

// For custom domains, ALWAYS use relative URLs
// For preview/localhost, use the env variable
const BACKEND_URL = isCustomDomain ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Debug log - will show in browser console
console.log('[Auth] Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'SSR');
console.log('[Auth] Is Custom Domain:', isCustomDomain);
console.log('[Auth] BACKEND_URL:', BACKEND_URL || '(empty - using relative)');
console.log('[Auth] API URL:', API);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      console.log('[Auth] Fetching current user from:', `${API}/auth/me`);
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('[Auth] Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('[Auth] Attempting login to:', `${API}/auth/login`);
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      console.log('[Auth] Login successful');
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      console.error('[Auth] Login failed:', error.response?.data || error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
